import { Trans } from '@lingui/react/macro';
import isNil from 'lodash/isNil';

import { Weight } from '@/api';
import { getBrowserLocale } from '@/services/i18n';

const formatNumber = (value?: number | null, options: Intl.NumberFormatOptions = {}) => {
  if (isNil(value)) {
    return '-';
  }

  const locale = getBrowserLocale();

  return new Intl.NumberFormat(locale, options).format(value);
};

export const formatPercentage = (value?: number | null) => {
  if (isNil(value)) {
    return '-';
  }

  return new Intl.NumberFormat(getBrowserLocale(), { style: 'percent' }).format(value / 100);
};

export const formatWeight = (
  weight?: Weight | null,
  options: Omit<Intl.NumberFormatOptions, 'style' | 'unit'> = {}
) => {
  if (!weight || !weight.amount) {
    return '-';
  }

  return formatNumber(weight.amount, {
    style: 'unit',
    unit: weight.unit,
    unitDisplay: 'short',
    maximumFractionDigits: 10, // We don't want to limit the number of digits here
    ...options,
  });
};

export const formatCurrency = (
  value?: number | null,
  currency?: string | null,
  options: Omit<Intl.NumberFormatOptions, 'currency' | 'style'> = {}
) => {
  if (isNil(value) || isNil(currency)) {
    return '-';
  }

  // We handle everything in cents.
  // In the future, we may have to handle zero decimal currencies.
  // See https://stripe.com/docs/currencies
  value /= 100;

  const locale = getBrowserLocale();

  return new Intl.NumberFormat(locale, {
    ...options,
    style: 'currency',
    currency,
  }).format(value);
};

export const formatCurrencyRange = (
  value?: { min?: number; max?: number; currency: string } | null,
  options: Omit<Intl.NumberFormatOptions, 'currency' | 'style'> = {}
) => {
  if (isNil(value)) {
    return '-';
  }

  const locale = getBrowserLocale();

  const i18n = new Intl.NumberFormat(locale, {
    ...options,
    style: 'currency',
    currency: value.currency,
  });

  if (value.min !== undefined && value.max !== undefined) {
    if ('formatRange' in i18n) {
      return i18n.formatRange(value.min / 100, value.max / 100);
    }

    return `${formatCurrency(value.min, value.currency, options)} - ${formatCurrency(value.max, value.currency, options)}`;
  } else if (value.min !== undefined) {
    return (
      <Trans id="utils.number.more-than">
        More than {formatCurrency(value.min, value.currency, options)}
      </Trans>
    );
  } else if (value.max !== undefined) {
    return (
      <Trans id="utils.number.less-than">
        Less than {formatCurrency(value.max, value.currency, options)}
      </Trans>
    );
  }

  return '-';
};

export const CURRENCIES = [
  'AED',
  'AFN',
  'ALL',
  'AMD',
  'AOA',
  'ARS',
  'AUD',
  'AWG',
  'AZN',
  'BAM',
  'BBD',
  'BDT',
  'BHD',
  'BIF',
  'BMD',
  'BND',
  'BOB',
  'BOV',
  'BRL',
  'BSD',
  'BTN',
  'BWP',
  'BYN',
  'BZD',
  'CAD',
  'CDF',
  'CHE',
  'CHF',
  'CHW',
  'CLF',
  'CLP',
  'CNY',
  'COP',
  'COU',
  'CRC',
  'CUP',
  'CVE',
  'CZK',
  'DJF',
  'DKK',
  'DOP',
  'DZD',
  'EGP',
  'ERN',
  'ETB',
  'EUR',
  'FJD',
  'FKP',
  'GBP',
  'GEL',
  'GHS',
  'GIP',
  'GMD',
  'GNF',
  'GTQ',
  'GYD',
  'HKD',
  'HNL',
  'HTG',
  'HUF',
  'IDR',
  'ILS',
  'INR',
  'IQD',
  'IRR',
  'ISK',
  'JMD',
  'JOD',
  'JPY',
  'KES',
  'KGS',
  'KHR',
  'KMF',
  'KPW',
  'KRW',
  'KWD',
  'KZT',
  'LAK',
  'LBP',
  'LRD',
  'LSL',
  'LYD',
  'MAD',
  'MGA',
  'MKD',
  'MMK',
  'MNT',
  'MOP',
  'MRU',
  'MUR',
  'MWK',
  'MXN',
  'MXV',
  'MYR',
  'MZN',
  'NAD',
  'NGN',
  'NIO',
  'NOK',
  'NPR',
  'NZD',
  'OMR',
  'PAB',
  'PEN',
  'PGK',
  'PHP',
  'PKR',
  'PLN',
  'PYG',
  'QAR',
  'RON',
  'RSD',
  'RUB',
  'RWF',
  'SAR',
  'SCR',
  'SDG',
  'SEK',
  'SGD',
  'SHP',
  'SLE',
  'SOS',
  'SRD',
  'SSP',
  'STN',
  'SVC',
  'SYP',
  'SZL',
  'THB',
  'TJS',
  'TMT',
  'TND',
  'TOP',
  'TRY',
  'TTD',
  'TWD',
  'TZS',
  'UAH',
  'UGX',
  'USD',
  'USN',
  'UYI',
  'UYW',
  'UYU',
  'UZS',
  'VED',
  'VES',
  'VND',
  'VUV',
  'WST',
  'XAD',
  'XAF',
  'XAG',
  'XAU',
  'XBA',
  'XBB',
  'XBC',
  'XBD',
  'XCD',
  'XCG',
  'XDR',
  'XOF',
  'XPD',
  'XPF',
  'XPT',
  'XSU',
  'XTS',
  'XUA',
  'XXX',
  'YER',
  'ZAR',
  'ZMW',
  'ZWG',
] as const;
export type Currency = (typeof CURRENCIES)[number];
